import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./andJobListing.module.css";
import { getJobs, submitJobProposal } from "../../redux/slices/jobPostingSlice";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import { notifyError, notifySuccess } from "../../utils";
import { ToastContainer } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import { use } from "react";
import LoginPopup from "../../components/LoginPopup";
import Skeleton from "../../components/skeleton/Skeleton";
import SkeletonWave from "../../components/skeleton/Skeleton";

const ANDJobListing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [jobs, setJobs] = useState([]);
  const {
    jobs,
    jobsLoading,
    submitProposalError,
    submitProposalLoading,
    submitProposalSuccess,
    totalJobs,
  } = useSelector((state) => state.job);
  const [page, setPage] = useState(1); // Default selected page
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const totalPages = useMemo(() => {
    return totalJobs % 10 === 0
      ? totalJobs / 10
      : Math.floor(totalJobs / 10) + 1;
  }, [totalJobs]);
  const user = useAuth();
  console.log("got jobs from store", jobs);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [portfolioLink, setPortfolioLink] = useState("");
  const [loading, setLoading] = useState(jobsLoading);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showBidModal, setShowBidModal] = useState(false);
  const [bidAmount, setBidAmount] = useState("");
  const [bidMessage, setBidMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    artistType: "all",
    budget: "all",
    location: "all",
    timeframe: "all",
  });

  // Sample artist types for filtering
  const artistTypes = [
    { id: "musician", label: "Musicians & Bands", icon: "🎶" },
    { id: "singer", label: "Singers", icon: "🎤" },
    { id: "singer", label: "Singers with Guitar", icon: "🎸" },
    { id: "photographers", label: "Photographers", icon: "📷" },
    { id: "videoEditors", label: "Videographers & Editors", icon: "🎬" },
    { id: "dancers", label: "Dancers & Choreographers", icon: "💃" },
    { id: "magicians", label: "Magicians & Illusionists", icon: "✨" },
    { id: "comedians", label: "Comedians & Entertainers", icon: "🎭" },
    { id: "djs", label: "DJs & Sound Artists", icon: "🎧" },
  ];

  // Budget ranges for filtering
  const budgetRanges = [
    { id: "all", label: "Any Budget" },
    { id: "under500", label: "Under 500" },
    { id: "500to1000", label: "500 - 1,000" },
    { id: "1000to2500", label: "1,000 - 2,500" },
    { id: "2500to5000", label: "2,500 - 5,000" },
    { id: "over5000", label: "Over 5,000" },
  ];

  // Timeframes for filtering
  const timeframes = [
    { id: "all", label: "Any Time" },
    { id: "urgent", label: "Urgent (< 1 week)" },
    { id: "soon", label: "Soon (< 1 month)" },
  ];

  // Mock jobs data - replace with API call in production
  useEffect(() => {
    // Simulate API call
    const payload = {
      page_number: page,
      page_size: 10,
    };
    if (filters?.artistType !== "all") {
      payload.category = filters?.artistType;
    }
    dispatch(getJobs(payload));
  }, [page, filters]);

  useEffect(() => {
    if (submitProposalSuccess) {
      notifySuccess("Proposal submitted!!");
      window.location.reload();

      closeBidModal();
    }
  }, [submitProposalSuccess]);

  useEffect(() => {
    if (submitProposalError) {
      notifyError("Something went wrong!!");
    }
  }, [submitProposalError]);

  // Filter jobs based on search and filters
  useEffect(() => {
    if (jobs?.length === 0) {
      setFilteredJobs([]);
      return;
    }

    let result = [...jobs];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        (job) =>
          job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job?.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply budget filter
    if (filters.budget !== "all") {
      switch (filters.budget) {
        case "under500":
          result = result.filter((job) => job.budget.min < 500);
          break;
        case "500to1000":
          result = result.filter(
            (job) => job.budget?.min >= 500 && job.budget?.max <= 1000
          );
          break;
        case "1000to2500":
          result = result.filter(
            (job) => job.budget?.min > 1000 && job.budget?.max <= 2500
          );
          break;
        case "2500to5000":
          result = result.filter(
            (job) => job.budget?.min > 2500 && job.budget?.max <= 5000
          );
          break;
        case "over5000":
          result = result.filter((job) => job.budget?.min > 5000);
          break;
        default:
          break;
      }
    }
    // Apply timeframe filter
    switch (filters.timeframe) {
      case "urgent":
        {
          const today = new Date();
          const oneWeekFromToday = new Date();
          oneWeekFromToday.setDate(today.getDate() + 7);

          result = result.filter((job) => {
            const startDate = new Date(job.startdate);
            return startDate < oneWeekFromToday;
          });
        }
        break;

      case "soon":
        {
          const today = new Date();
          const oneMonthFromToday = new Date();
          oneMonthFromToday.setDate(today.getDate() + 30); // Approx. 1 month

          result = result.filter((job) => {
            const startDate = new Date(job.startdate);
            return startDate < oneMonthFromToday;
          });
        }
        break;

      default:
        if (filters.timeframe !== "all") {
          result = result.filter((job) => job.timeframe === filters.timeframe);
        }
        break;
    }

    setFilteredJobs(result);
  }, [searchTerm, filters, jobs, page]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleChange = (event, value) => {
    setPage(value); // This updates the selected page
    console.log("Selected page:", value);
  };

  const handleFilterChange = (category, value) => {
    setFilters({
      ...filters,
      [category]: value,
    });
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
  };

  const handleCloseDetails = () => {
    setSelectedJob(null);
  };

  const openBidModal = (job) => {
    setSelectedJob(job);
    setShowBidModal(true);
  };

  const closeBidModal = () => {
    setShowBidModal(false);
    setBidAmount("");
    setBidMessage("");
    setPortfolioLink("");
  };

  function formatFullDate(isoDateString) {
    const date = new Date(isoDateString);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  }

  const handleBidSubmit = (e) => {
    e.preventDefault();

    // Here you would typically call your API to submit the bid
    if (submitProposalLoading) {
      return;
    }
    dispatch(
      submitJobProposal({
        job_id: selectedJob?.id,
        message: bidMessage,
        price: bidAmount,
        delivery_days: 2,
      })
    );

    // Close modal and reset form
    // closeBidModal();
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "INR",
    }).format(amount);
  };

  // Calculate days ago
  const getDaysAgo = (dateString) => {
    const postedDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - postedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  // Get artist type label
  const getArtistTypeLabel = (typeId) => {
    const type = artistTypes.find((type) => type.id === typeId);
    return type ? type.label : typeId;
  };

  // Get icon for artist type
  const getArtistTypeIcon = (typeId) => {
    switch (typeId) {
      case "musicians":
        return "🎸";
      case "singers":
        return "🎤";
      case "painters":
        return "🎨";
      case "photographers":
        return "📷";
      case "videoEditors":
        return "🎬";
      case "dancers":
        return "💃";
      case "magicians":
        return "✨";
      case "comedians":
        return "🎭";
      case "djs":
        return "🎧";
      case "writers":
        return "✍️";
      case "digitalArtists":
        return "💻";
      default:
        return "🌟";
    }
  };

  return (
    <div className={classes.jobListingsContainer}>
      <ToastContainer />
      {showLoginPopup && <LoginPopup />}
      <div className={classes.pageHeader}>
        <h1>Find Work</h1>
        <p> Let Your Talent 💰 Pay Off!</p>
        <p className={classes.orText}>OR</p>
        <button
          className={classes.postJobButton}
          onClick={() => navigate("/post-job")}
        >
          Post a Job
        </button>
        <p style={{ marginTop: "20px" }}>
          {" "}
          Post a Job Today and Connect with Top Talent ⭐️
        </p>
      </div>

      <div className={classes.filters}>
        <div className={classes.searchBar}>
          <input
            type="text"
            placeholder="Search jobs by title, description, or location..."
            value={searchTerm}
            onChange={handleSearchChange}
            className={classes.searchInput}
          />
          <span className={classes.searchIcon}>🔍</span>
        </div>

        <div className={classes.filterOptions}>
          <div className={classes.filterGroup}>
            <label>Artist Type</label>
            <select
              value={filters.artistType}
              onChange={(e) => handleFilterChange("artistType", e.target.value)}
              className={classes.filterSelect}
            >
              {artistTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className={classes.filterGroup}>
            <label>Budget</label>
            <select
              value={filters.budget}
              onChange={(e) => handleFilterChange("budget", e.target.value)}
              className={classes.filterSelect}
            >
              {budgetRanges.map((range) => (
                <option key={range.id} value={range.id}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          <div className={classes.filterGroup}>
            <label>Timeframe</label>
            <select
              value={filters.timeframe}
              onChange={(e) => handleFilterChange("timeframe", e.target.value)}
              className={classes.filterSelect}
            >
              {timeframes.map((time) => (
                <option key={time.id} value={time.id}>
                  {time.label}
                </option>
              ))}
            </select>
          </div>

          <button
            className={classes.resetButton}
            onClick={() =>
              setFilters({
                artistType: "all",
                budget: "all",
                location: "all",
                timeframe: "all",
              })
            }
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className={classes.resultsInfo}>
        {filteredJobs?.length || 0} opportunities found
      </div>

      <div className={classes.content}>
        <div className={classes.jobsList}>
          {jobsLoading ? (
            <div className={classes.loadingContainer}>
              <div className={classes.loader}></div>
              <p>Loading available jobs...</p>
            </div>
          ) : filteredJobs?.length === 0 ? (
            <div className={classes.noResults}>
              <div className={classes.noResultsIcon}>🔍</div>
              <h3>No matching jobs found</h3>
              <p>
                Try adjusting your filters or search terms to find more
                opportunities.
              </p>
              <button
                className={classes.resetButton}
                onClick={() => {
                  setSearchTerm("");
                  setFilters({
                    artistType: "all",
                    budget: "all",
                    location: "all",
                    timeframe: "all",
                  });
                }}
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            filteredJobs?.map((job) => (
              <>
                {job?.acceptedProposal ? null : (
                  <div
                    key={job.id}
                    className={classes.jobCard}
                    // onClick={() => handleJobClick(job)}
                    onClick={() => {
                      if (user?.isAuthenticated) {
                        openBidModal(job);
                      } else {
                        setShowLoginPopup(true);
                      }
                    }}
                  >
                    <div className={classes.jobHeader}>
                      <div className={classes.jobTitle}>{job.title}</div>
                      {job.isVerified && (
                        <div className={classes.verifiedBadge}>
                          <span className={classes.verifiedIcon}>✓</span>{" "}
                          Verified
                        </div>
                      )}
                    </div>

                    <div className={classes.jobMeta}>
                      <div className={classes.metaItem}>
                        <span className={classes.metaIcon}>
                          {getArtistTypeIcon(job.artist_type)}
                        </span>
                        <span>{getArtistTypeLabel(job.category)}</span>
                      </div>
                      <div className={classes.metaItem}>
                        <span className={classes.metaIcon}>📍</span>
                        <span>{job.address}</span>
                      </div>
                      <div className={classes.metaItem}>
                        <span className={classes.metaIcon}>💰</span>
                        <span>{`${job?.budget?.min || 100} - ${
                          job?.budget?.max || 200
                        } ${job?.currency || "INR"}`}</span>
                      </div>
                    </div>

                    <div className={classes.jobDescription}>
                      <span>Job Description: </span>
                      {job?.description?.length > 150
                        ? `${job.description.substring(0, 150)}...`
                        : job.description}
                    </div>

                    <div className={classes.jobFooter}>
                      <div className={classes.jobStats}>
                        <div className={classes.statItem}>
                          <span className={classes.statValue}>
                            {job?.total_proposals ||
                              Math.ceil(Math.random(1, 8) * 10)}
                          </span>
                          <span className={classes.statLabel}>Proposals</span>
                        </div>
                        <div className={classes.statItem}>
                          {job?.startdate && (
                            <>
                              <span className={classes.statValue}>
                                {formatFullDate(job.startdate)}
                              </span>
                              <span className={classes.statLabel}>
                                Start date
                              </span>
                            </>
                          )}
                        </div>
                        {job?.enddate && (
                          <>
                            <div className={classes.statItem}>
                              <span className={classes.statValue}>
                                {formatFullDate(job.enddate)}
                              </span>
                              <span className={classes.statLabel}>
                                Deadline date
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        className={classes.bidButton}
                        onClick={(e) => {
                          // e.stopPropagation();
                          // openBidModal(job);
                        }}
                      >
                        {user?.isAuthenticated
                          ? "Submit Proposal"
                          : "Login to Submit Proposal"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ))
          )}
        </div>

        {/* {selectedJob && !showBidModal && (
          <div className={classes.jobDetailsContainer}>
            <div className={classes.jobDetails}>
              <button
                className={classes.closeButton}
                onClick={handleCloseDetails}
              >
                &times;
              </button>

              <div className={classes.detailsHeader}>
                <h2>{selectedJob.title}</h2>
                {selectedJob.isVerified && (
                  <div className={classes.verifiedBadge}>
                    <span className={classes.verifiedIcon}>✓</span> Verified
                    Client
                  </div>
                )}
              </div>

              <div className={classes.clientInfo}>
                <div className={classes.clientAvatar}>
                  {selectedJob?.clientName?.charAt(0)}
                </div>
                <div className={classes.clientName}>
                  {selectedJob.clientName}
                </div>
              </div>

              <div className={classes.detailsSection}>
                <div className={classes.detailsGrid}>
                  <div className={classes.detailItem}>
                    <div className={classes.detailLabel}>Artist Type</div>
                    <div className={classes.detailValue}>
                      <span className={classes.detailIcon}>
                        {getArtistTypeIcon(selectedJob.artistType)}
                      </span>
                      {getArtistTypeLabel(selectedJob.artistType)}
                    </div>
                  </div>

                  <div className={classes.detailItem}>
                    <div className={classes.detailLabel}>Location</div>
                    <div className={classes.detailValue}>
                      <span className={classes.detailIcon}>📍</span>
                      {selectedJob.location}
                    </div>
                  </div>

                  <div className={classes.detailItem}>
                    <div className={classes.detailLabel}>Budget</div>
                    <div className={classes.detailValue}>
                      <span className={classes.detailIcon}>💰</span>
                      {formatCurrency(selectedJob.budget, selectedJob.currency)}
                    </div>
                  </div>

                  <div className={classes.detailItem}>
                    <div className={classes.detailLabel}>Posted</div>
                    <div className={classes.detailValue}>
                      <span className={classes.detailIcon}>📅</span>
                      {getDaysAgo(selectedJob.postedDate)} (
                      {new Date(selectedJob.postedDate).toLocaleDateString()})
                    </div>
                  </div>

                  <div className={classes.detailItem}>
                    <div className={classes.detailLabel}>Due Date</div>
                    <div className={classes.detailValue}>
                      <span className={classes.detailIcon}>⏰</span>
                      {new Date(selectedJob.dueDate).toLocaleDateString()}
                    </div>
                  </div>

                  <div className={classes.detailItem}>
                    <div className={classes.detailLabel}>Proposals</div>
                    <div className={classes.detailValue}>
                      <span className={classes.detailIcon}>👥</span>
                      {selectedJob.proposals} artists have applied
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.detailsContent}>
                <div className={classes.contentSection}>
                  <h3>Project Description</h3>
                  <p>{selectedJob.description}</p>
                </div>

                <div className={classes.contentSection}>
                  <h3>Requirements</h3>
                  <p>{selectedJob.requirements}</p>
                </div>
              </div>

              <div className={classes.actionButtons}>
                <button
                  className={classes.submitProposalButton}
                  onClick={() => openBidModal(selectedJob)}
                >
                  Submit a Proposal
                </button>
                <button
                  className={classes.saveButton}
                  onClick={() => alert("Job saved to your favorites!")}
                >
                  Save Job
                </button>
              </div>
            </div>
          </div>
        )} */}
      </div>

      {showBidModal && (
        <div className={classes.modalOverlay}>
          <div className={classes.bidModal}>
            <div className={classes.modalHeader}>
              <h2>Submit Proposal</h2>
              <button className={classes.closeButton} onClick={closeBidModal}>
                &times;
              </button>
            </div>

            <div className={classes.modalContent}>
              <div className={classes.jobSummary}>
                <div className={classes.summaryTitle}>{selectedJob.title}</div>
                <div className={classes.summaryBudget}>
                  Budget:{" "}
                  {`${selectedJob?.budget?.min} - ${selectedJob?.budget?.max} ${
                    selectedJob?.currency || "INR"
                  }`}
                </div>
              </div>

              <form onSubmit={handleBidSubmit} className={classes.bidForm}>
                <div className={classes.formGroup}>
                  <label className={classes.requiredLabel}>
                    Your Proposed Rate
                    <span>
                      {` (Currency : ${selectedJob?.currency || "INR"})`}
                    </span>
                  </label>

                  <div className={classes.inputGroup}>
                    {/* <select className={classes.currencySelect}>
                      <option value={selectedJob.currency}>
                        {selectedJob.currency}
                      </option>
                    </select> */}
                    <input
                      type="number"
                      min="0"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      placeholder="Enter your bid amount"
                      className={classes.bidInput}
                      required
                    />
                  </div>
                  <div className={classes.serviceFee}>
                    <span>Service Fee (5%):</span>
                    <span>
                      {bidAmount
                        ? `-${formatCurrency(
                            bidAmount * 0.05,
                            selectedJob?.currency
                          )}`
                        : "-0.00"}
                    </span>
                  </div>
                  <div className={classes.youllReceive}>
                    <span>You'll Receive:</span>
                    <span>
                      {bidAmount
                        ? formatCurrency(bidAmount * 0.95, selectedJob.currency)
                        : "0.00"}
                    </span>
                  </div>
                </div>

                <div className={classes.formGroup}>
                  <label className={classes.requiredLabel}>
                    Cover Letter / Proposal
                  </label>
                  <textarea
                    value={bidMessage}
                    onChange={(e) => setBidMessage(e.target.value)}
                    placeholder="Introduce yourself, highlight your relevant skills and experience, and explain why you're perfect for this job..."
                    className={classes.bidMessage}
                    rows={6}
                    required
                  ></textarea>
                </div>

                <div className={classes.attachmentsSection}>
                  <div className={classes.attachmentHeader}>
                    <h3>Add ArtistNextDoor Portfolio Link</h3>
                    <span>(Recommended)</span>
                  </div>
                  {/* <div className={classes.attachmentUploader}>
                    <div className={classes.dropZone}>
                      <div className={classes.uploadIcon}>📁</div>
                      <div className={classes.uploadText}>
                        Drag and drop files here or
                        <span className={classes.browseLink}>browse files</span>
                      </div>
                      <div className={classes.uploadSubtext}>
                        Supported formats: JPG, PNG, PDF, MP3, MP4 (Max 5MB)
                      </div>
                    </div>
                  </div> */}
                  <input
                    className={classes.portfolioLink}
                    value={portfolioLink}
                    onChange={(e) => setPortfolioLink(e.target.value)}
                    placeholder="Add ArtistNextDoor Portfolio Link Here to Catch an eye..."
                    required
                  ></input>
                </div>

                <div className={classes.bidActions}>
                  <button
                    type="button"
                    className={classes.cancelButton}
                    onClick={closeBidModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={classes.submitButton}>
                    {submitProposalLoading
                      ? "Submitting Proposal..."
                      : "Submit Proposal"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <div className={classes.pagination}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChange}
          color="secondary"
        />
      </div>
    </div>
  );
};

export default ANDJobListing;
